<template>
  <v-dialog v-model="dialog" scrollable max-width="500">
    <v-card class="edit-attivita-manuale">
      <v-toolbar dark :color="'var(--col-sport-'+sportId+')'">
        <v-toolbar-title>{{ isEdit ? 'Modifica attività' : 'Aggiungi attività' }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn dark icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-toolbar>

      <v-card-text class="pt-4">
        <v-btn v-if="hasTotalePianificato && !trimpAssegnato" small block class="mb-4" @click="compilaComeDaProgramma">Eseguito come pianificato</v-btn>

        <div class="campo mb-4">
          <label class="campo-label">Durata</label>
          <input-select format="hms" placeholder="hh:mm:ss" v-model="hms"></input-select>
        </div>

        <div class="campo mb-4">
          <label class="campo-label">Distanza</label>
          <div class="d-flex" style="gap: 8px;">
            <v-text-field type="number" min="0" :step="unitaDistanza === 'km' ? 0.001 : 1" v-model.number="distanzaInput" placeholder="Distanza" dense outlined hide-details></v-text-field>
            <v-select :items="['km','m']" v-model="unitaDistanza" dense outlined hide-details style="max-width: 88px;"></v-select>
          </div>
        </div>

        <div class="campo" v-if="!trimpAssegnato && sportHasZone">
          <label class="campo-label d-flex align-center" style="gap: 4px;">
            Tipologia di allenamento
            <v-btn icon x-small @click="infoTipologieAperto = true"><v-icon small>mdi-information-outline</v-icon></v-btn>
          </label>
          <v-select
            :items="tipologieOptions"
            v-model="tipologiaId"
            placeholder="Seleziona..."
            dense
            outlined
            hide-details
          ></v-select>

          <div v-if="tipologiaSelezionata" class="tipologia-preview mt-2">
            <div class="text-caption mb-1">{{ tipologiaSelezionata.didascalia }}</div>
            <div v-for="row in histogramRows(tipologiaSelezionata)" :key="row.label" class="d-flex align-center mb-1" style="gap: 8px;">
              <span class="zona-label">{{ row.label }}</span>
              <v-progress-linear background-color="var(--col-grigio-chiarissimo)" :value="row.percentuale" :color="row.colore" height="10" rounded style="flex: 1;"></v-progress-linear>
              <span class="zona-percentuale">{{ row.testo }}</span>
            </div>
          </div>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn v-if="trimpAssegnato" text @click="abilitaModificaTipologia">Modifica</v-btn>
        <v-btn color="primary" :disabled="salvando || !durataValida || !tipologiaValida" @click="salva">Conferma</v-btn>
      </v-card-actions>

      <v-dialog v-model="infoTipologieAperto" scrollable max-width="500">
        <v-card>
          <v-toolbar dark>
            <v-toolbar-title>Tipologia di allenamento</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn dark icon @click="infoTipologieAperto = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-toolbar>
          <v-card-text class="pt-4">
            <div v-for="t in tipologie" :key="t.codice" class="mb-4">
              <div class="font-weight-bold">{{ t.label }}</div>
              <div class="text-caption mb-1">{{ t.didascalia }}</div>
              <div v-for="row in histogramRows(t)" :key="row.label" class="d-flex align-center mb-1" style="gap: 8px;">
                <span class="zona-label">{{ row.label }}</span>
                <v-progress-linear background-color="var(--col-grigio-chiarissimo)"  :value="row.percentuale" :color="row.colore" height="10" rounded style="flex: 1;"></v-progress-linear>
                <span class="zona-percentuale">{{ row.testo }}</span>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "@mixins/api.js";
import InputSelect from "./InputSelect.vue";
import _funzioni from '@src/js/msp/funzioni.js';
import { classificaZoneDaTotalone } from '@src/js/msp/zone_fisiologiche.js';
import _zoneJson from '@cached/zone.json';
import _trimpsJson from '@cached/trimps.json';
import _sportJson from '@cached/sport.json';
import _tipologieJson from '@config/commons/tipologia_allenamento_manuale.json';

// Modal per creazione/modifica di un'attività manuale (device: 'manuale') in
// t_sessioni_allenamento_v3_hdr. Equivalente mobile di
// frontend-v3/src/components/EditAttivitaManuale.vue, stesso comportamento (select
// "Tipologia di allenamento", zone/trimp derivati dalle sue percentuali statiche, "Eseguito
// come pianificato" che copia anche la distribuzione di zone del piano da
// contesto.zonePianificate, json_zone{tipo}/json_zoneFisiologiche salvati allo stesso modo) ma
// con markup Vuetify invece che Tailwind/msp-dialog — nessun componente .vue condiviso tra i
// due, vedi commenti nel gemello desktop per il dettaglio del calcolo (identico qui, stesso
// codice quasi copia-incolla salvo l'adattamento di api()/import). L'unica differenza di
// comportamento reale: l'informativa su tutte le tipologie è un v-dialog nidificato (icona "i"
// accanto alla label, vedi infoTipologieAperto) invece del popover MspTooltip.vue del desktop, e
// le barre dell'istogramma sono v-progress-linear invece di MspGraficoBarre.vue (entrambi
// componenti locali di frontend-v3, non disponibili qui).
//
// Sostituisce il vecchio flusso "feedback manuale" (durata/distanza inserite a mano su
// allenamento_feedback.secondi/metri, in allenamentoFeedback.vue): ora anche da mobile il
// completamento senza device passa da qui, creando un'attività vera e propria.
//
// Apertura/chiusura tramite ref (open()/close()), come MspConfirm.vue. In creazione (prop
// `attivita` assente) fa una POST bare su attivita.php che crea l'attività "libera" (non
// associata); il chiamante (MspAllenamentoDetail.vue) è responsabile di associarla
// all'allenamento riusando lo stesso flusso già esistente per "Associa attività"
// (selectAttivita/associaAttivita). In modifica (prop `attivita` con .id) fa una PUT su
// attivita.php?id=..., generico per qualunque colonna della tabella.
const ROOT_API = process.env.VUE_APP_API_BASE_URL + '/api/v2';

// sport_id di "nuoto" (src/cached/json/sport.json), risolto per codice invece che hardcoded:
// serve a scegliere lo schema di zone da mostrare/salvare, vedi isNuoto e
// tipologia_allenamento_manuale.json (zone Z1..Z5 generiche vs zoneNuoto A1..C1)
const SPORT_NUOTO = _sportJson.find(s => s.codice === 'nuoto');
const SPORT_ID_NUOTO = SPORT_NUOTO ? SPORT_NUOTO.id : null;

const component = {
    components: {
        InputSelect,
    },
    props: {
        // presente in modifica: {id, durata (sec), metri, trimp, sport_id, atleta_id, data}
        attivita: {
            type: Object,
            default: null,
        },
        // usato solo in creazione, per popolare i campi non editabili nel modal
        // (titolo/sport/sportId/atleta_id/data dell'allenamento a cui verrà associata
        // l'attività, e i totali pianificati per "Eseguito come pianificato")
        contesto: {
            type: Object,
            default: () => ({}),
        },
    },
    data: function () {
        return {
            dialog: false,
            hms: '',
            distanzaMetri: null,
            unitaDistanza: 'km',
            // chiave della tipologia scelta (vedi tipologia_allenamento_manuale.json), null se
            // non ancora selezionata
            tipologiaId: null,
            // 'potenza'|'passo'|'cardio', da api_tipoZonaAttivo (vedi caricaZonaTipo): decide in
            // quale colonna json_zone{tipo} salvare la distribuzione di zone al salvataggio
            zonaTipo: null,
            infoTipologieAperto: false,
            trimp: 0,
            // true dopo "Eseguito come pianificato": il trimp è il valore pianificato, non più
            // derivato dalla tipologia scelta (vedi ricalcolaTrimp), e la select tipologia si
            // nasconde. Si riparte dal calcolo normale solo riaprendo il modal (reset()) — qui
            // non c'è un bottone "cambia tipologia" per uscirne, il flusso mobile è
            // volutamente a due sole scelte: come da programma, o inserimento libero
            trimpAssegnato: false,
            salvando: false,
        };
    },
    computed: {
        isEdit: function () {
            return !!(this.attivita && this.attivita.id);
        },
        durataValida: function () {
            return !!(this.hms && _funzioni.hms2sec(this.hms) > 0);
        },
        // con trimp assegnato (bottone "Eseguito come pianificato") la tipologia non serve: il
        // trimp arriva già dal totale pianificato, non da una distribuzione di zone. Idem per
        // gli sport senza zone di allenamento (has_zone in sport.json, es. palestra): la select
        // tipologia è nascosta (vedi sportHasZone) quindi non può mai essere valorizzata
        tipologiaValida: function () {
            return this.trimpAssegnato || !this.sportHasZone || !!this.tipologiaId;
        },
        sportId: function () {
            return (this.attivita && this.attivita.sport_id) || this.contesto.sportId || null;
        },
        // false per gli sport senza zone di allenamento (has_zone in src/cached/json/sport.json,
        // es. palestra): la select "Tipologia di allenamento" non ha senso, non esiste una
        // distribuzione di zone da derivare (vedi tipologiaValida/distribuzioneZone)
        sportHasZone: function () {
            const sport = this.sportId && _sportJson.find(s => +s.id === +this.sportId);
            return !!(sport && sport.has_zone);
        },
        atletaId: function () {
            return (this.attivita && this.attivita.atleta_id) || this.contesto.atleta_id || null;
        },
        dataAllenamento: function () {
            const raw = (this.attivita && this.attivita.data) || this.contesto.data || '';
            return raw ? String(raw).substring(0, 10) : '';
        },
        // il nuoto ha un proprio schema di zone (A1, A2, B1, B2, C1) invece del generico
        // Z1..Z5 (vedi tipologia_allenamento_manuale.json, zoneNuoto): usato da
        // distribuzioneZone/histogramRows per scegliere la lista giusta
        isNuoto: function () {
            return !!(this.sportId && SPORT_ID_NUOTO && +this.sportId === +SPORT_ID_NUOTO);
        },
        hasTotalePianificato: function () {
            return !!(this.contesto && this.contesto.durataTotale);
        },
        distanzaInput: {
            get: function () {
                if (this.distanzaMetri === null || this.distanzaMetri === undefined) return null;
                return this.unitaDistanza === 'km' ? _funzioni.round(this.distanzaMetri / 1000,1) : this.distanzaMetri;
            },
            set: function (val) {
                if (val === null || val === '' || isNaN(val)) {
                    this.distanzaMetri = null;
                    return;
                }
                this.distanzaMetri = Math.round(this.unitaDistanza === 'km' ? val * 1000 : val);
            },
        },
        durataSec: function () {
            return this.hms ? _funzioni.hms2sec(this.hms) : 0;
        },
        // elenco delle tipologie disponibili, ordinate come nel documento sorgente
        // (tipologia_allenamento_manuale.json, ordine come da sketchbook/attivita-manuali.txt)
        tipologie: function () {
            return Object.keys(_tipologieJson)
                .map(codice => Object.assign({codice: codice}, _tipologieJson[codice]))
                .sort((a, b) => a.ordine - b.ordine);
        },
        // stesso elenco, nel formato richiesto da v-select ({text, value})
        tipologieOptions: function () {
            return this.tipologie.map(t => ({text: t.label, value: t.codice}));
        },
        tipologiaSelezionata: function () {
            return this.tipologiaId ? _tipologieJson[this.tipologiaId] : null;
        },
        // distribuisce durataSec sulle zone della tipologia scelta (Z1..Z5, o A1..C1 per il
        // nuoto — vedi isNuoto), secondo le sue percentuali statiche (niente soglie/passo: la
        // tipologia dà già la distribuzione). Include SEMPRE tutte le zone, anche quelle a
        // percentuale 0 (valore/trimp 0) — stesso comportamento di FitManager::a_init_arr_zone
        // (le attività elaborate da FIT hanno sempre tutte le zone del test, comprese quelle a
        // 0): un array sparso farebbe apparire "buchi" nell'istogramma di attivita_dettaglio
        // quando la tipologia salta zone intermedie (es. "Allenamento polarizzato": Z2 e Z5 ma
        // non Z3/Z4) — analisiAllenamento_calcolaZone in attivita.php itera l'array così com'è,
        // zone assenti = barre assenti, non barre a 0. Ogni voce porta anche il proprio trimp
        // (non arrotondato, come FitManager::calcolaTrimpZone): {codice, valore, trimp}, stesso
        // formato delle attività elaborate da FIT.
        distribuzioneZone: function () {
            if (!this.tipologiaSelezionata || !this.durataSec || !this.sportId) return [];
            const lista = this.isNuoto ? this.tipologiaSelezionata.zoneNuoto : this.tipologiaSelezionata.zone;
            return lista.map(z => {
                const valore = Math.round(this.durataSec * z.percentuale / 100);
                return {
                    codice: z.codice,
                    valore: valore,
                    trimp: this.trimpZona(z.codice, valore),
                };
            });
        },
        // idem per le zone fisiologiche ZA/ZB/ZC, in secondi (vedi json_zoneFisiologiche nel
        // commento in testa al file). Non c'è un campo "fisiologiche" a percentuali fisse nella
        // tipologia (rimosso 2026-08-27, era scritto a mano e divergeva dalle percentuali di
        // "zone"/"zoneNuoto" — vedi anche che per il nuoto i codici A1/A2/B1/B2 aggregano su
        // Z1/Z2/Z4/Z5, non su Z1..Z4 come si potrebbe pensare guardando solo l'ordine, quindi
        // un'unica percentuale valida per tutti gli sport non è comunque corretta): si riclassifica
        // invece distribuzioneZone zona per zona con la stessa mappa statica del backend
        // (Z1/Z2→ZA, Z3→ZB, Z4→50% ZB/50% ZC tranne B1 del nuoto tutta in ZB, Z5..Z7→ZC — vedi
        // ZoneFisiologiche::classificaZoneDaTotalone in PHP e classificaZoneDaTotalone in
        // src/js/msp/zone_fisiologiche.js, già usata altrove per lo stesso scopo, es.
        // SchedaAtleta/Fasi.vue).
        distribuzioneFisiologiche: function () {
            if (!this.distribuzioneZone.length || !this.sportId) return null;
            const accumulator = classificaZoneDaTotalone(this.distribuzioneZone.map(z => {
                const zona = _zoneJson.find(zz => +zz.sport_id === +this.sportId && zz.codice === z.codice);
                return { id: zona ? zona.id : null, sec: z.valore };
            }));
            return {
                ZA: Math.round(accumulator.ZA),
                ZB: Math.round(accumulator.ZB),
                ZC: Math.round(accumulator.ZC),
            };
        },
        // distribuzione di zone dell'allenamento pianificato, da contesto.zonePianificate (il
        // totalone dell'allenamento) — usata al posto di distribuzioneZone quando
        // trimpAssegnato (bottone "Eseguito come pianificato"), vedi gemello desktop.
        // totalone.zone NON è già aggregato per codice: è un elenco con una riga per ogni
        // blocco/serie del piano che tocca quella zona, quindi va sommato qui per codice prima
        // di poterlo salvare nello stesso formato {codice, valore, trimp} di distribuzioneZone
        // — trimp per riga è già calcolato lato server, si somma senza ricalcolo.
        distribuzioneZonePianificate: function () {
            const zone = this.contesto && this.contesto.zonePianificate && this.contesto.zonePianificate.zone;
            if (!zone || !zone.length) return [];
            const perCodice = {};
            zone.forEach(z => {
                if (!z.codice || z.codice === 'Z0') return;
                if (!perCodice[z.codice]) perCodice[z.codice] = { codice: z.codice, valore: 0, trimp: 0 };
                perCodice[z.codice].valore += Math.round(z.sec || 0);
                perCodice[z.codice].trimp += z.trimp || 0;
            });
            return Object.values(perCodice);
        },
        // idem per le zone fisiologiche: totalone.zoneFisiologiche è già nel formato
        // {ZA,ZB,ZC} in secondi, già aggregato lato server — solo arrotondamento.
        distribuzioneFisiologichePianificate: function () {
            const zf = this.contesto && this.contesto.zonePianificate && this.contesto.zonePianificate.zoneFisiologiche;
            if (!zf) return null;
            return {
                ZA: Math.round(zf.ZA || 0),
                ZB: Math.round(zf.ZB || 0),
                ZC: Math.round(zf.ZC || 0),
            };
        },
    },
    watch: {
        hms: function () {
            this.ricalcolaTrimp();
        },
        tipologiaId: function () {
            this.ricalcolaTrimp();
        },
    },
    methods: {
        open: function () {
            this.reset();
            this.dialog = true;
        },
        close: function () {
            this.dialog = false;
        },
        reset: function () {
            const a = this.attivita || {};
            this.hms = a.durata ? _funzioni.sec2hms(a.durata) : '';
            this.distanzaMetri = a.metri ? +a.metri : null;
            // la tipologia con cui è stata salvata l'attività non è persistita (solo la
            // distribuzione di zone risultante): si riparte sempre da nessuna tipologia
            // selezionata, sia in creazione che in modifica
            this.tipologiaId = null;
            this.zonaTipo = null;
            this.trimpAssegnato = false;
            this.ricalcolaTrimp();
            this.caricaZonaTipo();
        },
        // converte le percentuali di una tipologia nel formato richiesto dalle
        // v-progress-linear, riusando i colori già definiti per le zone (src/cached/json/
        // zone.json, colonna colore). tipologia.zone/zoneNuoto elencano già tutte le zone
        // dello schema, comprese quelle a percentuale 0 (i "—" del documento sorgente): serve
        // per confrontare a colpo d'occhio quali zone coinvolge ciascuna tipologia, non solo
        // quelle attive.
        histogramRows: function (tipologia) {
            if (!tipologia) return [];
            const lista = this.isNuoto ? tipologia.zoneNuoto : tipologia.zone;
            return lista.map(z => {
                // Z1..Z5 sono generici (codice_aggregazione, condiviso da ogni sport); A1..C1
                // sono invece i codici reali del nuoto (sport_id di SPORT_ID_NUOTO in
                // zone.json), non un'aggregazione.
                const zona = this.isNuoto
                    ? _zoneJson.find(zz => zz.sport_id === SPORT_ID_NUOTO && zz.codice === z.codice)
                    : _zoneJson.find(zz => zz.codice_aggregazione === z.codice);
                return {
                    label: z.codice,
                    percentuale: z.percentuale,
                    colore: zona ? zona.colore : undefined,
                    testo: z.percentuale ? z.percentuale + '%' : '—',
                };
            });
        },
        // determina in quale colonna json_zone{tipo} salvare la distribuzione di zone al
        // salvataggio (vedi commento in testa al file e api_tipoZonaAttivo in attivita.php)
        caricaZonaTipo: function () {
            this.zonaTipo = null;
            if (!this.sportId || !this.atletaId) return;
            let url = ROOT_API + '/attivita.php?tipoZonaAttivo&sport_id=' + this.sportId + '&atleta_id=' + this.atletaId;
            if (this.dataAllenamento) {
                url += '&data=' + encodeURIComponent(this.dataAllenamento);
            }
            api(url, 'GET').then((result) => {
                if (result.success && result.data && result.data.tipo) {
                    this.zonaTipo = result.data.tipo;
                }
            });
        },
        // trimp di una singola zona (durata_zona in minuti * coefficiente trimp per
        // sport+zona, src/cached/json/trimps.json), non arrotondato — stessa formula di
        // FitManager::calcolaTrimpZone (a_calcola_coeff_trimp(zona) * valore/60). Match per
        // "codice" (non codice_aggregazione): per il nuoto codice è già il codice reale
        // (A1..C1, vedi isNuoto/distribuzioneZone), che coincide con codice_aggregazione solo
        // per gli altri sport (Z1..Z5 self-mapping).
        trimpZona: function (codice, valoreSec) {
            if (!this.sportId) return 0;
            const zona = _zoneJson.find(zz => +zz.sport_id === +this.sportId && zz.codice === codice);
            if (!zona) return 0;
            const t = _trimpsJson.find(tt => +tt.sport_id === +this.sportId && +tt.zona_id === zona.id);
            if (!t) return 0;
            return (valoreSec / 60) * t.trimp;
        },
        // trimp totale: somma dei trimp di zona già calcolati in distribuzioneZone (arrotondato
        // solo qui, per il campo intero "trimp" della tabella hdr) — sostituisce il vecchio
        // calcolo su una singola zona di default
        ricalcolaTrimp: function () {
            // con trimp assegnato (bottone "Eseguito come pianificato") il valore è fisso,
            // non derivato dalla tipologia
            if (this.trimpAssegnato) return;
            if (!this.distribuzioneZone.length) {
                this.trimp = 0;
                return;
            }
            const trimpTotale = this.distribuzioneZone.reduce((sum, z) => sum + z.trimp, 0);
            this.trimp = Math.round(trimpTotale);
        },
        // compila durata, distanza e trimp con i totali dell'allenamento pianificato (contesto.
        // durataTotale/distanzaTotale/trimpTotale), per il caso "l'atleta ha svolto
        // l'allenamento esattamente come pianificato". Marca trimpAssegnato prima di tutto:
        // ricalcolaTrimp (richiamato dai watcher su hms/tipologiaId) non sovrascrive più il
        // trimp assegnato, e la select tipologia si nasconde (vedi commento in testa al file).
        compilaComeDaProgramma: function () {
            if (!this.hasTotalePianificato) return;
            this.trimpAssegnato = true;
            this.tipologiaId = null;
            this.hms = _funzioni.sec2hms(this.contesto.durataTotale);
            if (this.contesto.distanzaTotale) {
                this.distanzaMetri = Math.round(this.contesto.distanzaTotale);
            }
            this.trimp = this.contesto.trimpTotale;
        },
        // tasto "Modifica" (visibile solo con trimpAssegnato, accanto a "Conferma"):
        // unico modo per uscire da "Eseguito come pianificato" senza chiudere e riaprire il
        // modal. Riporta il form allo stato di modifica libera (tipologia selezionabile di
        // nuovo, "Eseguito come pianificato" di nuovo visibile — stesso stato di un'apertura
        // normale) mantenendo durata/distanza già compilate dal totale pianificato — solo il
        // trimp torna 0 finché non si sceglie una tipologia (niente più valore assegnato da
        // cui derivarlo).
        abilitaModificaTipologia: function () {
            this.trimpAssegnato = false;
            this.tipologiaId = null;
            this.ricalcolaTrimp();
        },
        salva: function () {
            if (this.salvando || !this.durataValida || !this.tipologiaValida) return;
            this.salvando = true;

            const durataSec = this.hms ? _funzioni.hms2sec(this.hms) : 0;
            const metri = Math.round(this.distanzaMetri || 0);
            const payload = {
                durata: durataSec,
                metri: metri,
                trimp: this.trimp,
            };

            // distribuzione zone/zone fisiologiche dalla tipologia scelta, oppure — con trimp
            // assegnato (bottone "Eseguito come pianificato") — dall'allenamento pianificato
            // stesso (distribuzioneZonePianificate/distribuzioneFisiologichePianificate, vedi
            // commento in testa al file): in entrambi i casi assente senza un tipo di test
            // attivo per l'atleta (zonaTipo, da caricaZonaTipo). Stesso formato scritto
            // dall'elaborazione FIT: json_zone{tipo} è [{zona, valore, trimp}, ...],
            // json_zoneFisiologiche è {[tipo]: {ZA,ZB,ZC}} — multi-tipo anche con un solo tipo.
            const zoneDaSalvare = this.trimpAssegnato ? this.distribuzioneZonePianificate : this.distribuzioneZone;
            const fisiologicheDaSalvare = this.trimpAssegnato ? this.distribuzioneFisiologichePianificate : this.distribuzioneFisiologiche;
            if (this.zonaTipo && zoneDaSalvare.length) {
                payload.zonaTipo = this.zonaTipo;
                payload.zoneJson = zoneDaSalvare.map(z => ({zona: z.codice, valore: z.valore, trimp: z.trimp}));
                payload.zoneFisiologiche = {[this.zonaTipo]: fisiologicheDaSalvare};
            }

            let request;
            if (this.isEdit) {
                request = api(ROOT_API + '/attivita.php?id=' + this.attivita.id, 'PUT', payload);
            } else {
                Object.assign(payload, {
                    titoloAllenamento: this.contesto.titolo || '',
                    sport: (this.contesto.sport || '').toUpperCase(),
                    gruppoSport: (this.contesto.gruppoSport || '').toUpperCase(),
                    idSportCP: this.contesto.sportId || null,
                    dataAllenamento: this.contesto.data || '',
                    // atleta di destinazione: necessario quando è l'allenatore a creare
                    // l'attività per conto di un suo atleta (senza, il backend intesterebbe
                    // l'attività all'allenatore stesso, vedi api_add in attivita.php)
                    atleta_id: this.atletaId,
                });
                request = api(ROOT_API + '/attivita.php', 'POST', payload);
            }

            request.then((result) => {
                this.salvando = false;
                if (!result.success) return;
                const id = this.isEdit ? this.attivita.id : result.data;
                this.$emit('saved', { id: id, isEdit: this.isEdit });
                this.close();
            });
        },
    },
};
export default component;
</script>

<style lang="scss">
.edit-attivita-manuale {
  .campo-label {
    display: block;
    font-size: 0.8em;
    font-weight: bold;
    margin-bottom: 4px;
  }
  .zona-label {
    width: 28px;
    font-size: 0.75em;
  }
  .zona-percentuale {
    width: 36px;
    font-size: 0.75em;
    text-align: right;
  }
}
</style>
